import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { captureException } from "@sentry/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavBarTitle } from "components/NavBar";
import { useAppData } from "store/appData";
import { executeObjectTypeMerge, previewObjectTypeMerge } from "./api";
import ConfirmMergeDialog from "./ConfirmMergeDialog";
import ObjectTypePicker from "./ObjectTypePicker";
import PreviewSummaryTable from "./PreviewSummaryTable";
import RegionResultList from "./RegionResultList";
import type {
  ObjectTypeMergeExecuteResult,
  ObjectTypeMergePreview,
} from "./schemas";
import t from "./translations";

// Deliberate friction on an irreversible action — the button stays locked
// for a few seconds after the preview shows up, so the admin has to at
// least look at the row counts before it's clickable.
const MERGE_LOCK_SECONDS = 15;

export default function ObjectTypeMerge() {
  const { objectTypes } = useAppData();

  const [sourceTypeId, setSourceTypeId] = useState<number | null>(null);
  const [targetTypeId, setTargetTypeId] = useState<number | null>(null);

  const [preview, setPreview] = useState<ObjectTypeMergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [executeResult, setExecuteResult] =
    useState<ObjectTypeMergeExecuteResult | null>(null);

  const [mergeLockSeconds, setMergeLockSeconds] = useState(0);

  useNavBarTitle(t.PageTitle);

  const sameType =
    sourceTypeId !== null && targetTypeId !== null && sourceTypeId === targetTypeId;
  const canPreview = sourceTypeId !== null && targetTypeId !== null && !sameType;
  const canMerge = preview !== null && canPreview && mergeLockSeconds === 0;

  useEffect(() => {
    if (!preview) {
      setMergeLockSeconds(0);
      return;
    }
    setMergeLockSeconds(MERGE_LOCK_SECONDS);
    const interval = setInterval(() => {
      setMergeLockSeconds((seconds) => Math.max(0, seconds - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [preview]);

  function handleTypeChange(
    setTypeId: (typeId: number | null) => void,
    typeId: number | null,
  ) {
    setTypeId(typeId);
    setPreview(null);
    setExecuteResult(null);
  }

  async function handlePreview() {
    if (sourceTypeId === null || targetTypeId === null) return;
    setPreviewLoading(true);
    setPreview(null);
    setExecuteResult(null);
    try {
      const result = await previewObjectTypeMerge(sourceTypeId, targetTypeId);
      setPreview(result);
    } catch (error) {
      captureException(error, { event_id: "objectTypeMerge.preview" });
      toast.error(t.PreviewError);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleConfirmMerge() {
    if (sourceTypeId === null || targetTypeId === null || preview === null) return;
    setMerging(true);
    try {
      const result = await executeObjectTypeMerge(sourceTypeId, targetTypeId);
      setExecuteResult(result);
      setConfirmOpen(false);
      if (result.overall_status === "ok") toast.success(t.MergeSuccess);
      else if (result.overall_status === "partial_failure")
        toast.error(t.MergePartialFailure);
      else toast.error(t.MergeFailed);
    } catch (error) {
      captureException(error, { event_id: "objectTypeMerge.execute" });
      toast.error(t.MergeError);
    } finally {
      setMerging(false);
    }
  }

  return (
    <Box sx={{ p: 4 }} data-testid="object-type-merge-page">
      <Box sx={{ maxWidth: 960, mx: "auto" }}>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mb: 3 }}
        >
          {t.Intro}
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <ObjectTypePicker
              label={t.SourceTypeLabel}
              value={sourceTypeId}
              onChange={(typeId) => handleTypeChange(setSourceTypeId, typeId)}
              excludeTypeId={targetTypeId}
            />
            <ArrowForwardIcon color="action" sx={{ mt: 1.5 }} />
            <ObjectTypePicker
              label={t.TargetTypeLabel}
              value={targetTypeId}
              onChange={(typeId) => handleTypeChange(setTargetTypeId, typeId)}
              excludeTypeId={sourceTypeId}
            />
          </Box>
          <Button
            variant="outlined"
            disabled={!canPreview || previewLoading}
            onClick={handlePreview}
            startIcon={
              previewLoading ? <CircularProgress size={16} /> : undefined
            }
          >
            {t.PreviewButton}
          </Button>
        </Box>

        {sameType && (
          <Typography color="error" variant="body2" align="center" sx={{ mb: 2 }}>
            {t.SameTypeError}
          </Typography>
        )}
      </Box>

      {preview && (
        <Box sx={{ mb: 3 }}>
          <PreviewSummaryTable preview={preview} />
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              disabled={!canMerge}
              onClick={() => setConfirmOpen(true)}
              data-testid="objectTypeMerge.openConfirm"
            >
              {mergeLockSeconds > 0
                ? t.MergeButtonLocked(mergeLockSeconds)
                : t.MergeButton}
            </Button>
          </Box>
        </Box>
      )}

      {executeResult && (
        <Box sx={{ maxWidth: 960, mx: "auto" }}>
          <RegionResultList result={executeResult} />
        </Box>
      )}

      {preview && sourceTypeId !== null && targetTypeId !== null && (
        <ConfirmMergeDialog
          open={confirmOpen}
          sourceName={objectTypes[sourceTypeId]?.en_name ?? ""}
          targetName={objectTypes[targetTypeId]?.en_name ?? ""}
          submitting={merging}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handleConfirmMerge}
        />
      )}
    </Box>
  );
}
